import React, { createContext, useContext, useState, useEffect } from 'react';

const CartContext = createContext(null);

const CART_KEY = 'kreeo_cart';
const FAVORITES_KEY = 'kreeo_favorites';

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState({ items: [], total: 0 });
  const [favorites, setFavorites] = useState({ product_ids: [] });

  useEffect(() => {
    const savedCart = localStorage.getItem(CART_KEY);
    const savedFavorites = localStorage.getItem(FAVORITES_KEY);
    if (savedCart) {
      try {
        setCart(JSON.parse(savedCart));
      } catch (e) {
        setCart({ items: [], total: 0 });
      }
    }
    if (savedFavorites) {
      try {
        setFavorites(JSON.parse(savedFavorites));
      } catch (e) {
        setFavorites({ product_ids: [] });
      }
    }
  }, []);

  const saveCart = (newCart) => {
    setCart(newCart);
    localStorage.setItem(CART_KEY, JSON.stringify(newCart));
  };

  const saveFavorites = (newFavorites) => {
    setFavorites(newFavorites);
    localStorage.setItem(FAVORITES_KEY, JSON.stringify(newFavorites));
  };

  const addToCart = async (product_id, quantity = 1, customization = {}, productData = null) => {
    const newItems = [...cart.items];
    const existingIndex = newItems.findIndex((i) => i.product_id === product_id);

    if (existingIndex >= 0) {
      newItems[existingIndex].quantity += quantity;
    } else if (productData) {
      newItems.push({
        product_id,
        name: productData.name,
        price: productData.price,
        quantity,
        customization,
        image: productData.images && productData.images[0] ? productData.images[0] : ''
      });
    } else {
      return { success: false, error: 'Product data required' };
    }

    const newTotal = newItems.reduce((acc, item) => acc + item.price * item.quantity, 0);
    saveCart({ items: newItems, total: newTotal });
    return { success: true };
  };

  const removeFromCart = async (product_id) => {
    const newItems = cart.items.filter((i) => i.product_id !== product_id);
    const newTotal = newItems.reduce((acc, item) => acc + item.price * item.quantity, 0);
    saveCart({ items: newItems, total: newTotal });
  };

  const updateQuantity = async (product_id, quantity) => {
    if (quantity < 1) {
      return removeFromCart(product_id);
    }
    const newItems = cart.items.map((i) =>
      i.product_id === product_id ? { ...i, quantity } : i
    );
    const newTotal = newItems.reduce((acc, item) => acc + item.price * item.quantity, 0);
    saveCart({ items: newItems, total: newTotal });
  };

  const clearCart = async () => {
    saveCart({ items: [], total: 0 });
  };

  const toggleFavorite = async (product_id) => {
    const newIds = favorites.product_ids.includes(product_id)
      ? favorites.product_ids.filter((id) => id !== product_id)
      : [...favorites.product_ids, product_id];
    saveFavorites({ product_ids: newIds });
  };

  const isFavorite = (product_id) => {
    return favorites.product_ids.includes(product_id);
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        favorites,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        toggleFavorite,
        isFavorite
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};
